import { Alert, Badge, Button, EmptyState, Field, Input, Skeleton } from '@personachat/ui';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Power, PowerOff, Trash2, UsersRound } from 'lucide-react';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { api } from '../../api/client';
import { lire, messageDe } from '../../api/call';
import type { components } from '../../api/schema';
import { Code, ErreurChargement, InfoList, Jauge, Section, Select, StatutBadge, TypeBadge } from '../../components/bits';
import { DataTable, type Colonne } from '../../components/DataTable';
import { Drawer } from '../../components/Drawer';
import { ConfirmDialog } from '../../components/Modal';
import { TabPanel, Tabs } from '../../components/Tabs';
import { useToast } from '../../components/Toast';
import { bornesJourParis, formatDate, formatDateTime, formatNombre, jourParis } from '../../lib/format';
import { LIBELLE_TYPE, statutCle, TON_TYPE } from '../../lib/labels';
import { AuditTimeline } from '../audit/AuditTimeline';
import { useEvenements } from '../events/EventsPage';

type Eleve = components['schemas']['UserListItem'];
type Onglet = 'details' | 'eleves' | 'journal';

/** Fiche d'une clé d'accès (cahier des charges §7.4) : modification, élèves inscrites, journal. */
export function AccessKeyDrawer() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const onglet = (['details', 'eleves', 'journal'].includes(params.get('onglet') ?? '') ? params.get('onglet') : 'details') as Onglet;

  const cle = useQuery({
    queryKey: ['cle-acces', id],
    queryFn: async () => (await lire(api.GET('/api/admin/access-keys/{id}', { params: { path: { id } } }))).accessKey,
  });

  const fermer = () => {
    const s = new URLSearchParams(location.search);
    s.delete('onglet');
    navigate(`/cles-acces${s.toString() ? `?${s}` : ''}`);
  };

  const k = cle.data;
  const statut = k ? statutCle(k) : null;

  return (
    <Drawer
      onClose={fermer}
      label={k ? `Clé ${k.key}` : 'Clé d’accès'}
      title={k ? <Code valeur={k.key} /> : <Skeleton width={200} height={22} />}
      subtitle={k?.description ?? undefined}
      badges={
        k && statut ? (
          <>
            <Badge tone={TON_TYPE[k.keyType]}>Crée des comptes {LIBELLE_TYPE[k.keyType]}</Badge>
            <Badge tone={statut.ton}>{statut.libelle}</Badge>
          </>
        ) : undefined
      }
    >
      {cle.isError ? (
        <div style={{ paddingTop: 16 }}>
          <ErreurChargement erreur={cle.error} onReessayer={() => void cle.refetch()} />
        </div>
      ) : !k ? (
        <Skeleton height={200} />
      ) : (
        <>
          <Tabs
            label="Sections de la clé"
            actif={onglet}
            onChange={(o) =>
              setParams(
                (a) => {
                  const s = new URLSearchParams(a);
                  if (o === 'details') s.delete('onglet');
                  else s.set('onglet', o);
                  return s;
                },
                { replace: true }
              )
            }
            onglets={[
              { id: 'details', label: 'Détails' },
              { id: 'eleves', label: 'Élèves inscrites', count: k._count.users },
              { id: 'journal', label: 'Journal' },
            ]}
          />
          <TabPanel label={onglet === 'details' ? 'Détails' : onglet === 'eleves' ? 'Élèves inscrites' : 'Journal'}>
            {onglet === 'details' && <DetailsCle cle={k} onSupprimee={fermer} />}
            {onglet === 'eleves' && <ElevesCle id={k.id} />}
            {onglet === 'journal' && <AuditTimeline targetId={k.id} />}
          </TabPanel>
        </>
      )}
    </Drawer>
  );
}

type FicheCle = components['schemas']['AccessKey'] & { _count: { users: number }; creator: { id: string; email: string } | null };

function DetailsCle({ cle, onSupprimee }: { cle: FicheCle; onSupprimee: () => void }) {
  const id = useId();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [description, setDescription] = useState(cle.description ?? '');
  const [maxUses, setMaxUses] = useState(String(cle.maxUses));
  const [expiration, setExpiration] = useState(cle.expiresAt ? jourParis(new Date(cle.expiresAt)) : '');
  const [evenement, setEvenement] = useState(cle.eventId ?? '');
  const evenements = useEvenements();
  const evenementsDuType = (evenements.data ?? []).filter((e) => e.userType === cle.keyType);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [confirmer, setConfirmer] = useState<'statut' | 'supprimer' | null>(null);

  useEffect(() => {
    setDescription(cle.description ?? '');
    setMaxUses(String(cle.maxUses));
    setExpiration(cle.expiresAt ? jourParis(new Date(cle.expiresAt)) : '');
    setEvenement(cle.eventId ?? '');
  }, [cle.description, cle.maxUses, cle.expiresAt, cle.eventId]);

  const rafraichir = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['cle-acces', cle.id] }),
      queryClient.invalidateQueries({ queryKey: ['cles-acces'] }),
      queryClient.invalidateQueries({ queryKey: ['audit', cle.id] }),
      queryClient.invalidateQueries({ queryKey: ['alertes'] }),
      queryClient.invalidateQueries({ queryKey: ['evenements'] }),
    ]);

  const expirationActuelle = cle.expiresAt ? jourParis(new Date(cle.expiresAt)) : '';
  const changements = {
    ...(description.trim() !== (cle.description ?? '') ? { description: description.trim() || null } : {}),
    ...(Number(maxUses) !== cle.maxUses ? { maxUses: Number(maxUses) } : {}),
    ...(expiration !== expirationActuelle ? { expiresAt: expiration ? bornesJourParis(expiration, 'fin') : null } : {}),
    ...(evenement !== (cle.eventId ?? '') ? { eventId: evenement || null } : {}),
  };
  const aChange = Object.keys(changements).length > 0;
  const maxInvalide = !Number.isInteger(Number(maxUses)) || Number(maxUses) < Math.max(1, cle.usedCount);

  const enregistrer = async (e: FormEvent) => {
    e.preventDefault();
    if (!aChange || maxInvalide) return;
    setEnvoi(true);
    setErreur(null);
    try {
      await lire(api.PATCH('/api/admin/access-keys/{id}', { params: { path: { id: cle.id } }, body: changements }));
      await rafraichir();
      toast.succes('Clé modifiée.');
    } catch (err) {
      setErreur(messageDe(err));
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <>
      <Section title="Utilisation">
        <InfoList
          items={[
            { label: 'Utilisations', value: <Jauge utilise={cle.usedCount} max={cle.maxUses} /> },
            { label: 'Type de compte créé', value: <Badge tone={TON_TYPE[cle.keyType]}>{LIBELLE_TYPE[cle.keyType]}</Badge> },
            {
              label: 'Événement',
              hidden: cle.keyType === 'NORMAL',
              value: cle.event ? (
                <Link className="link" to={`/evenements/${cle.event.id}`}>
                  {cle.event.name}
                </Link>
              ) : (
                <span className="muted">Aucun choisi : l’événement le plus récent de ce type</span>
              ),
            },
            { label: 'Expiration', value: cle.expiresAt ? formatDateTime(cle.expiresAt) : 'Aucune' },
            { label: 'Création', value: `${formatDateTime(cle.createdAt)}${cle.creator ? ` par ${cle.creator.email}` : ''}` },
          ]}
        />
      </Section>

      <Section title="Modifier">
        <form className="stack" onSubmit={enregistrer}>
          <Field id={`${id}-desc`} label="Cohorte (description)">
            <Input id={`${id}-desc`} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={200} />
          </Field>
          <div className="grid-2">
            <Field
              id={`${id}-max`}
              label="Utilisations maximum"
              error={maxInvalide ? `Au moins ${formatNombre(Math.max(1, cle.usedCount))} (déjà utilisée ${formatNombre(cle.usedCount)} fois).` : null}
            >
              <Input id={`${id}-max`} inputMode="numeric" value={maxUses} onChange={(e) => setMaxUses(e.target.value.replace(/[^\d]/g, ''))} invalid={maxInvalide} />
            </Field>
            <Field id={`${id}-exp`} label="Expire le" hint="Vide : sans expiration. Valable jusqu’à 23:59, heure de Paris.">
              <Input id={`${id}-exp`} type="date" value={expiration} onChange={(e) => setExpiration(e.target.value)} />
            </Field>
          </div>
          {cle.keyType !== 'NORMAL' && evenementsDuType.length > 0 && (
            <Field id={`${id}-evenement`} label="Événement" hint="Les élèves déjà inscrites avec cette clé suivent aussi ce changement : le module de l’événement leur est ouvert.">
              <Select id={`${id}-evenement`} value={evenement} onChange={(e) => setEvenement(e.target.value)}>
                <option value="">Le plus récent de ce type</option>
                {evenementsDuType.map((e) => (
                  <option key={e.id} value={e.id}>
                    {e.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          {erreur && <Alert>{erreur}</Alert>}
          <div className="row">
            <Button type="submit" variant="primary" loading={envoi} disabled={!aChange || maxInvalide}>
              Enregistrer
            </Button>
            {aChange && (
              <Button
                variant="ghost"
                onClick={() => {
                  setDescription(cle.description ?? '');
                  setMaxUses(String(cle.maxUses));
                  setExpiration(expirationActuelle);
                  setEvenement(cle.eventId ?? '');
                }}
              >
                Annuler
              </Button>
            )}
          </div>
        </form>
      </Section>

      <Section title="Actions">
        <div className="actions-list">
          {cle.isActive ? (
            <Button icon={<PowerOff size={15} aria-hidden="true" />} onClick={() => setConfirmer('statut')}>
              Désactiver
            </Button>
          ) : (
            <Button icon={<Power size={15} aria-hidden="true" />} onClick={() => setConfirmer('statut')}>
              Activer
            </Button>
          )}
          {cle.usedCount === 0 && (
            <Button variant="danger" icon={<Trash2 size={15} aria-hidden="true" />} onClick={() => setConfirmer('supprimer')}>
              Supprimer
            </Button>
          )}
        </div>
        {cle.usedCount > 0 && <p className="note">Une clé déjà utilisée ne se supprime pas : désactivez-la.</p>}
      </Section>

      <ConfirmDialog
        open={confirmer === 'statut'}
        onOpenChange={(o) => !o && setConfirmer(null)}
        title={cle.isActive ? 'Désactiver la clé' : 'Activer la clé'}
        confirmLabel={cle.isActive ? 'Désactiver' : 'Activer'}
        tone={cle.isActive ? 'danger' : 'primary'}
        onConfirm={async () => {
          await lire(api.PATCH('/api/admin/access-keys/{id}', { params: { path: { id: cle.id } }, body: { isActive: !cle.isActive } }));
          await rafraichir();
          toast.succes(cle.isActive ? 'Clé désactivée.' : 'Clé activée.');
        }}
      >
        <p>
          {cle.isActive
            ? 'La clé sera refusée à l’inscription. Les comptes déjà créés avec elle ne changent pas.'
            : 'La clé sera de nouveau acceptée à l’inscription, dans la limite de ses utilisations et de son expiration.'}
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={confirmer === 'supprimer'}
        onOpenChange={(o) => !o && setConfirmer(null)}
        title="Supprimer la clé"
        confirmLabel="Supprimer"
        tone="danger"
        retaper={cle.key}
        onConfirm={async () => {
          await lire(api.DELETE('/api/admin/access-keys/{id}', { params: { path: { id: cle.id } } }));
          await queryClient.invalidateQueries({ queryKey: ['cles-acces'] });
          toast.succes('Clé supprimée.');
          onSupprimee();
        }}
      >
        <p>La clé, jamais utilisée, est supprimée définitivement.</p>
      </ConfirmDialog>
    </>
  );
}

function ElevesCle({ id }: { id: string }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const eleves = useQuery({
    queryKey: ['cle-acces', id, 'eleves', page],
    queryFn: () => lire(api.GET('/api/admin/access-keys/{id}/users', { params: { path: { id }, query: { page, limit: 20 } } })),
    placeholderData: keepPreviousData,
  });
  const colonnes: Colonne<Eleve>[] = [
    {
      id: 'eleve',
      header: 'Élève',
      cell: (e) => (
        <div>
          <div className="cell-main">{e.fullName ?? e.email}</div>
          <div className="cell-sub">{e.email}</div>
        </div>
      ),
    },
    { id: 'type', header: 'Type', cell: (e) => <TypeBadge type={e.userType} isAdmin={e.isAdmin} /> },
    { id: 'statut', header: 'Statut', cell: (e) => <StatutBadge isActive={e.isActive} deletedAt={e.deletedAt} /> },
    { id: 'inscription', header: 'Inscription', cell: (e) => formatDate(e.createdAt) },
  ];
  if (eleves.isError) return <ErreurChargement erreur={eleves.error} onReessayer={() => void eleves.refetch()} />;
  return (
    <DataTable
      libelle="Élèves inscrites avec la clé"
      colonnes={colonnes}
      lignes={eleves.data?.users}
      cleLigne={(e) => e.id}
      chargement={eleves.isFetching && !eleves.data}
      onOuvrir={(e) => navigate(`/eleves/${e.id}`)}
      pagination={eleves.data ? { page: eleves.data.pagination.page, pages: eleves.data.pagination.pages, total: eleves.data.pagination.total } : undefined}
      onPage={setPage}
      vide={<EmptyState icon={<UsersRound size={24} aria-hidden="true" />} title="Aucune inscription avec cette clé" />}
    />
  );
}
